import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  createAuthenticatedApi,
  EMAIL_ALREADY_IN_USE_MESSAGE,
  getApiErrorMessage,
  throwIfResultFailed,
} from "@/lib/utils";
import { UsersApi } from "@/openapi/generated";
import type { CreateUserRequest } from "@/openapi/generated/models";

export const useAdminCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createUserRequest: CreateUserRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(UsersApi, accessToken);
      const result = await service.apiUserCreateAdminPost({
        createUserRequest,
      });

      throwIfResultFailed(result, "Failed to create administrator");
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Administrator created",
        description: "They'll receive an email to set a password and sign in.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });

      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersByRoleGet],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersScrollGet],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.UserGet],
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to create administrator",
        { conflictFallback: EMAIL_ALREADY_IN_USE_MESSAGE },
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
